import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { userAPI } from '../../services/api';
import { MemberProfile } from '../../types/memberProfile';

interface CreateGuestAccountDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the new guest MemberProfile after successful creation. */
    onSuccess: (guestProfile: MemberProfile) => void;
}

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const empty: FormData = { firstName: '', lastName: '', email: '', phone: '' };

export const CreateGuestAccountDialog: React.FC<CreateGuestAccountDialogProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const [form, setForm] = useState<FormData>(empty);
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = () => {
        if (loading) return;
        setForm(empty);
        setErrors({});
        setError(null);
        onClose();
    };

    const validate = (): boolean => {
        const errs: Partial<FormData> = {};
        if (!form.firstName.trim()) errs.firstName = 'First name is required';
        if (!form.lastName.trim()) errs.lastName = 'Last name is required';
        if (!form.email.trim()) {
            errs.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errs.email = 'Enter a valid email address';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        setError(null);

        try {
            // Single atomic call — backend creates User + guest MemberProfile together.
            const response = await userAPI.createUser({
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || undefined,
                role: 'guest',
                sendWelcomeEmail: false,
            });

            const guestProfile: MemberProfile = response.data.memberProfile;

            onSuccess(guestProfile);
            handleClose();
        } catch (err: any) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                'Failed to create guest account';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleChange =
        (field: keyof FormData) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }));
            if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
        };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                Create Guest Account
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Creates a guest user account and an unlinked guest profile. You can link this
                    profile to a guest pass at any time.
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="First Name"
                            value={form.firstName}
                            onChange={handleChange('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            fullWidth
                            required
                            disabled={loading}
                            autoFocus
                        />
                        <TextField
                            label="Last Name"
                            value={form.lastName}
                            onChange={handleChange('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            fullWidth
                            required
                            disabled={loading}
                        />
                    </Box>

                    <TextField
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        fullWidth
                        required
                        disabled={loading}
                    />

                    <TextField
                        label="Phone (optional)"
                        value={form.phone}
                        onChange={handleChange('phone')}
                        fullWidth
                        disabled={loading}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
                >
                    {loading ? 'Creating…' : 'Create Guest Account'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateGuestAccountDialog;
